/**
 * Policy templates with merge fields.
 * 
 * Templates are stored in the database (policy_templates table) and managed
 * by super_admins. The constants below serve as fallbacks if DB fetch fails.
 * 
 * Available merge fields:
 *   {{bankName}}          - Tenant display name
 *   {{privacyEmail}}      - DPO / privacy contact email
 *   {{retentionDays}}     - Photo retention period in days
 *   {{retentionPolicy}}   - Human-readable retention description
 *   {{freeAttempts}}      - Free avatar generation attempts
 *   {{effectiveDate}}     - Policy effective date
 */

export const FALLBACK_PRIVACY_TEMPLATE = `<h2>Privacy Policy</h2>
<p><strong>Effective Date:</strong> {{effectiveDate}}</p>
<p>{{bankName}} provides a personalised card service in partnership with Marketspringpad IP USA LLC. Please contact your bank for the full privacy policy.</p>`;

export const FALLBACK_TERMS_TEMPLATE = `<h2>Terms & Conditions</h2>
<p><strong>Effective Date:</strong> {{effectiveDate}}</p>
<p>{{bankName}} provides a personalised card service in partnership with Marketspringpad IP USA LLC. Please contact your bank for the full terms and conditions.</p>`;

/** All available merge fields with descriptions for the editor UI */
export const MERGE_FIELDS = [
  { key: "bankName", description: "Bank / tenant display name" },
  { key: "privacyEmail", description: "Privacy / DPO contact email" },
  { key: "retentionDays", description: "Photo retention period (days)" },
  { key: "retentionPolicy", description: "Human-readable retention description" },
  { key: "freeAttempts", description: "Free avatar generation attempts" },
  { key: "effectiveDate", description: "Policy effective date" },
] as const;

/**
 * Merge fields into a policy template, returning rendered HTML.
 */
export function renderPolicyTemplate(
  template: string,
  fields: Record<string, string>
): string {
  return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return fields[key] ?? match;
  });
}

/**
 * Build the merge fields object from tenant configuration.
 */
export function buildPolicyFields(tenant: {
  name: string;
  notificationEmail?: string;
  freeAttemptsPerUser?: number;
  photoRetentionEnabled?: boolean;
  photoRetentionDays?: number;
}): Record<string, string> {
  const retentionEnabled = tenant.photoRetentionEnabled ?? false;
  const retentionDays = tenant.photoRetentionDays ?? 7;

  let retentionPolicy: string;
  if (retentionEnabled) {
    retentionPolicy = `Your uploaded photos are automatically deleted ${retentionDays} day${retentionDays !== 1 ? "s" : ""} after the print package has been downloaded by the Bank.`;
  } else {
    retentionPolicy = `Your uploaded photos are retained on our servers until the Bank chooses to delete them. You may request deletion at any time.`;
  }

  return {
    bankName: tenant.name,
    privacyEmail: tenant.notificationEmail || "privacy@bank.com",
    retentionDays: String(retentionDays),
    retentionPolicy,
    freeAttempts: String(tenant.freeAttemptsPerUser ?? 5),
    effectiveDate: "1 January 2026",
  };
}
